<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-2xl">
      {{ this.$route.meta.title }}
    </p>

    <!-- Barge Route Optimize -->
    <div
      v-if="isBusyBargeOverview"
      class="p-3 w-full flex flex-col gap-5 bg-white rounded-lg shadow-md mt-3"
    >
      <vs-progress
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
    </div>
    <kpi-card
      v-show="!isBusyBargeOverview"
      type="barge_route_optimize"
      :data="bargeOverviewData"
      :chart-options="chartOptions"
      :chart-plugins="chartPlugins"
      :pie-filter.sync="bargeOverviewFilter.month_year_pie_chart"
      :rank-filter.sync="bargeOverviewFilter.month_year_rank"
      :average-pie-chart-title="'Average of Operation Cost (Cost/Ton)'"
    />
    <!-- Barge Monitoring -->
    <div
      v-if="isBusyBargeMonitoring"
      class="p-3 w-full flex flex-col gap-5 bg-white rounded-lg shadow-md mt-3"
    >
      <vs-progress
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
    </div>
    <kpi-card
      v-show="!isBusyBargeMonitoring"
      type="barge_monitoring"
      :data="bargeMonitoringData"
      :chart-plugins="chartPlugins"
      :chart-options="chartOptions"
      :pie-filter.sync="bargeMonitoringFilter.month_year_pie_chart"
      :rank-filter.sync="bargeMonitoringFilter.month_year_rank"
      :average-pie-chart-title="'Average of Operation Time (%, Days)'"
    />
    <!-- Fuel Optimization -->
    <div
      v-if="isBusyFuelOptimization"
      class="p-3 w-full flex flex-col gap-5 bg-white rounded-lg shadow-md mt-3"
    >
      <vs-progress
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
    </div>
    <kpi-card
      v-show="!isBusyFuelOptimization"
      type="fuel_optimization"
      :data="fuelOptimizationData"
      :chart-options="chartOptions"
      :chart-plugins="chartPlugins"
      :has-average-pie-chart="false"
      :rank-filter.sync="fuelOptimizationFilter.month_year_rank"
    />
    <!-- Loading Capacity Monitoring -->
    <div
      v-if="isBusyLoadingMonitoring"
      class="p-3 w-full flex flex-col gap-5 bg-white rounded-lg shadow-md mt-3"
    >
      <vs-progress
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
    </div>
    <kpi-card
      v-show="!isBusyLoadingMonitoring"
      type="loading_capacity_utilization"
      :data="loadingMonitoringData"
      :chart-options="chartOptions"
      :chart-plugins="chartPlugins"
      :pie-filter.sync="loadingMonitoringFilter.month_year_pie_chart"
      :rank-filter.sync="loadingMonitoringFilter.month_year_rank"
      :average-pie-chart-title="'Average of Cargo Loading Target (%/Trips)'"
    />
  </div>
</template>

<script>
import {mapGetters} from "vuex";

import { PAGE_TITLE } from "@/fixtures/Constants";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";
import KpiCard from "@/views/kpiTracking/components/KpiCard.vue";

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    KpiCard
  },
  data() {
    return {
      bargeMonitoringData: null,
      bargeOverviewData: null,
      fuelOptimizationData: null,
      loadingMonitoringData: null,
      isBusyBargeMonitoring: false,
      isBusyBargeOverview: false,
      isBusyFuelOptimization: false,
      isBusyLoadingMonitoring: false,
      bargeOverviewFilter: {
        month_year_pie_chart: null,
        month_year_rank: null
      },
      fuelOptimizationFilter: {
        month_year_rank: null
      },
      loadingMonitoringFilter: {
        month_year_pie_chart: null,
        month_year_rank: null
      },
      bargeMonitoringFilter: {
        month_year_pie_chart: null,
        month_year_rank: null
      }
    }
  },
  computed: {
    ...mapGetters({
      bargeOverview: 'BargeOverviewKpi/bargeOverview',
      fuelOptimization: 'FuelOptimizationKpi/fuelOptimization',
      loadingMonitoring: 'LoadingMonitoringKpi/loadingMonitoring',
      bargeMonitoring: 'BargeMonitoringKpi/bargeMonitoring'
    }),
    chartPlugins() {
      return {
        line: [
          this.customFillPlugin(),
          this.customCanvasBackgroundColor()
        ],
        doughnut: [
          this.htmlLegendPlugin(),
          this.drawPercentagePlugin()
        ],
        bar: [
          this.drawBarValuePlugin(),
          this.drawBarLabelPlugin()
        ]
      }
    },
    chartOptions() {
      return {
        line: this.lineChartOptions(),
        doughnut: this.doughnutChartOptions(),
        bar: this.barChartOptions()
      }
    }
  },
  watch: {
    bargeOverview: {
      immediate: true,
      handler(val) {
        if (val) {
          this.bargeOverviewData = val
        }
      }
    },
    bargeOverviewFilter: {
      deep: true,
      handler() {
        this.fetchBargeOverview()
      }
    },
    bargeMonitoring: {
      immediate: true,
      handler(val) {
        if (val) {
          this.bargeMonitoringData = val
        }
      }
    },
    bargeMonitoringFilter: {
      deep: true,
      handler() {
        this.fetchBargeMonitoring()
      }
    },
    fuelOptimization: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fuelOptimizationData = val
        }
      }
    },
    fuelOptimizationFilter: {
      deep: true,
      handler() {
        this.fetchFuelOptimization()
      }
    },
    loadingMonitoring: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadingMonitoringData = val
        }
      }
    },
    loadingMonitoringFilter: {
      deep: true,
      handler() {
        this.fetchLoadingMonitoring()
      }
    }
  },
  mounted() {
    this.fetchBargeOverview()
    this.fetchFuelOptimization()
    this.fetchLoadingMonitoring()
    this.fetchBargeMonitoring()
  },
  methods: {
    async fetchBargeOverview() {
      this.isBusyBargeOverview = true
      try {
        const filter = this.bargeOverviewFilter
        await this.$store.dispatch('BargeOverviewKpi/fetchBargeOverview', filter)
        this.isBusyBargeOverview = false
      } catch (error) {
        this.isBusyBargeOverview = false
        this.showFailedNotification(this, 'Failed to fetch Barge Overview KPI, please try again')
      }
    },
    async fetchBargeMonitoring() {
      this.isBusyBargeMonitoring = true
      try {
        const filter = this.bargeMonitoringFilter
        await this.$store.dispatch('BargeMonitoringKpi/fetchBargeMonitoring', filter)
        this.isBusyBargeMonitoring = false
      } catch (error) {
        this.isBusyBargeMonitoring = false
        this.showFailedNotification(this, 'Failed to fetch Barge Monitoring KPI, please try again')
      }
    },
    async fetchFuelOptimization() {
      this.isBusyFuelOptimization = true
      try {
        const filter = this.fuelOptimizationFilter
        await this.$store.dispatch('FuelOptimizationKpi/fetchFuelOptimization', filter)
        this.isBusyFuelOptimization = false
      } catch (error) {
        this.isBusyFuelOptimization = false
        this.showFailedNotification(this, 'Failed to fetch Fuel Optimization KPI, please try again')
      }
    },
    async fetchLoadingMonitoring() {
      this.isBusyLoadingMonitoring = true
      try {
        const filter = this.loadingMonitoringFilter
        await this.$store.dispatch('LoadingMonitoringKpi/fetchLoadingMonitoring', filter)
        this.isBusyLoadingMonitoring = false
      } catch (error) {
        this.isBusyLoadingMonitoring = false
        this.showFailedNotification(this, 'Failed to fetch Loading Monitoring KPI, please try again')
      }
    },
    customFillPlugin() {
      return {
        id: 'customFill',
        beforeDraw(chart, args, options) {
          const { ctx, chartArea: { left, right, top, bottom }, scales: { x, y } } = chart
          const meta = chart.getDatasetMeta(0)
          const points = meta.data
          const lastIndex = points.length - 1

          ctx.fillStyle = '#F8F8F8'
          ctx.fillRect(
            0,
            0,
            points[lastIndex].x - x.left,
            chart.height
          )

          const pointDistance = ((right - left) / (meta.data.length - 1)) / 2
          const blueFillStartX = points[lastIndex].x - pointDistance
          ctx.fillStyle = '#E0ECFB'
          ctx.fillRect(
            blueFillStartX,
            0,
            x.right - blueFillStartX,
            chart.height
          )
        }
      }
    },
    customCanvasBackgroundColor() {
      return {
        id: 'customCanvasBackgroundColor',
        beforeDraw: (chart, args, options) => {
          const {ctx} = chart;
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = options.color || '#E0ECFB';
          ctx.fillRect(0, 0, chart.width, chart.height);
          ctx.restore();
        }
      }
    },
    htmlLegendPlugin() {
      return {
        id: 'htmlLegend',
        getOrCreateLegendList(chart, id) {
          const legendContainer = document.getElementById(id);
          let listContainer = legendContainer.querySelector('ul');

          if (!listContainer) {
            listContainer = document.createElement('ul');
            listContainer.style.display = 'flex';
            listContainer.style.flexDirection = 'column';
            listContainer.style.alignItems = 'flex-start';
            listContainer.style.margin = 0;
            listContainer.style.padding = 0;

            legendContainer.appendChild(listContainer);
          }

          return listContainer;
        },
        afterUpdate(chart, args, options) {
          const ul = this.getOrCreateLegendList(chart, options.containerID);

          // Remove old legend items
          while (ul.firstChild) {
            ul.firstChild.remove();
          }

          // Reuse the built-in legendItems generator
          const items = chart.options.plugins.legend.labels.generateLabels(chart);

          items.forEach(item => {
            const li = document.createElement('li');
            li.style.alignItems = 'center';
            li.style.display = 'flex';
            li.style.flexDirection = 'row';
            li.style.marginLeft = '10px';
            li.style.gap = '10px';

            // Color box
            const boxSpan = document.createElement('span');
            boxSpan.style.background = item.fillStyle;
            boxSpan.style.borderColor = item.strokeStyle;
            boxSpan.style.borderWidth = `${item.lineWidth}px`
            boxSpan.style.display = 'inline-block';
            boxSpan.style.flexShrink = 0;
            boxSpan.style.height = '10px';
            boxSpan.style.marginRight = '0px';
            boxSpan.style.width = '34px';
            boxSpan.style.borderRadius = '5px';

            // Text
            const textContainer = document.createElement('p');
            textContainer.style.color = item.fontColor;
            textContainer.style.margin = 0;
            textContainer.style.padding = 0;
            textContainer.style.textDecoration = item.hidden ? 'line-through' : '';
            textContainer.style.fontSize = '12px'

            const text = document.createTextNode(item.text);
            textContainer.appendChild(text);

            li.appendChild(boxSpan);
            li.appendChild(textContainer);
            ul.appendChild(li);
          });
        }
      }
    },
    drawPercentagePlugin() {
      return {
        id: 'drawPercentage',
        afterDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
          const meta = chart.getDatasetMeta(0);

          meta.data.forEach((arc, index) => {
            const value = data.datasets[0].data[index];
            const calculated = value.toFixed(1)
            const percentage = `${calculated}%`;

            const {x, y} = arc.tooltipPosition(); // center of each arc

            ctx.save();
            ctx.fillStyle = '#fff';
            ctx.font = '600 12px Poppins';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(percentage, x, y);
            ctx.restore();
          });
        }
      }
    },
    drawBarValuePlugin() {
      return {
        id: 'drawBarValue',
        afterDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const meta = chart.getDatasetMeta(0);

          meta.data.forEach((arc, index) => {
            const value = handleValidateAndFormatNumber(data.datasets[0].data[index].value, 2)

            const {x, y, width} = arc; // center of each arc

            ctx.save();
            ctx.fillStyle = '#fff';
            ctx.font = 'normal 11px Poppins';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(value, x - width + 10, y);
            ctx.restore();
          });
        }
      }
    },
    drawBarLabelPlugin() {
      return {
        id: 'drawBarLabel',
        afterDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const meta = chart.getDatasetMeta(0);

          meta.data.forEach((arc, index) => {
            const value = data.datasets[0].data[index]['key'];
            const {x, y, width, height, base} = arc

            ctx.save();
            ctx.fillStyle = '#000';
            ctx.font = '600 11px Poppins';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(value, base, y + height / 2 + 7);
            ctx.restore();
          });
        }
      }
    },
    lineChartOptions() {
      return {
        responsive: true,
        stacked: false,
        maintainAspectRatio: false,
        scales: {
          x: {
            display: false,
            offset: true
          },
          y: {
            type: 'linear',
            display: false,
            offset: true,
            grid: {
              drawOnChartArea: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true
          }
        }
      }
    },
    doughnutChartOptions() {
      return {
        responsive: true,
        cutout: '60%',
        borderWidth: 0,
        plugins: {
          legend: {
            display: false
          }
        }
      }
    },
    barChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        indexAxis: 'y',
        parsing: {
          xAxisKey: 'value',
          yAxisKey: 'key'
        },
        scales: {
          x: {
            type: 'logarithmic',
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false
            }
          },
          y: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              callback(value, index) {
                return `${index + 1}`;
              }
            }
          }
        },
        plugins: {
          legend: {
            display: false
          }
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
